import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, AlertCircle, XCircle, Loader } from 'lucide-react';

interface SpecReviewProgressProps {
    projectId: string;
}

interface ProgressData {
    total: number;
    ready: number;
    queued: number;
    blocked: number;
    failed: number;
    pending: number;
    percentage: number;
    blocked_reasons: Record<string, number>;
}

const SpecReviewProgress: React.FC<SpecReviewProgressProps> = ({ projectId }) => {
    const [progress, setProgress] = useState<ProgressData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showBlockedDetails, setShowBlockedDetails] = useState(false);

    useEffect(() => {
        loadProgress();
        // Poll every 5 seconds while there are queued items
        const interval = setInterval(() => {
            if (progress && progress.queued > 0) {
                loadProgress();
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [projectId, progress?.queued]);

    const loadProgress = async () => {
        try {
            const response = await fetch(`/api/spec-review/projects/${projectId}/spec-review/progress`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setProgress(data);
            }
        } catch (error) {
            console.error('Error loading spec review progress:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-center">
                    <Loader className="w-6 h-6 animate-spin text-blue-500" />
                    <span className="ml-2 text-gray-600">Loading spec review progress...</span>
                </div>
            </div>
        );
    }

    if (!progress || progress.total === 0) {
        return null;
    }

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Spec Review Progress</h3>
                <span className="text-2xl font-bold text-blue-600">{progress.percentage}%</span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
                <div
                    className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${progress.percentage}%` }}
                />
            </div>

            {/* Status Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                {/* Ready */}
                <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                        <div className="text-xs text-gray-600">Ready</div>
                        <div className="text-lg font-semibold text-green-700">{progress.ready}</div>
                    </div>
                </div>

                {/* Queued */}
                <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600 animate-pulse" />
                    <div>
                        <div className="text-xs text-gray-600">Queued</div>
                        <div className="text-lg font-semibold text-blue-700">{progress.queued}</div>
                    </div>
                </div>

                {/* Blocked */}
                <div
                    className="flex items-center space-x-2 p-3 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors"
                    onClick={() => setShowBlockedDetails(!showBlockedDetails)}
                >
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    <div>
                        <div className="text-xs text-gray-600">Blocked</div>
                        <div className="text-lg font-semibold text-yellow-700">{progress.blocked}</div>
                    </div>
                </div>

                {/* Failed */}
                <div className="flex items-center space-x-2 p-3 bg-red-50 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <div>
                        <div className="text-xs text-gray-600">Failed</div>
                        <div className="text-lg font-semibold text-red-700">{progress.failed}</div>
                    </div>
                </div>

                {/* Pending */}
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                    <Loader className="w-5 h-5 text-gray-600" />
                    <div>
                        <div className="text-xs text-gray-600">Pending</div>
                        <div className="text-lg font-semibold text-gray-700">{progress.pending}</div>
                    </div>
                </div>
            </div>

            {/* Blocked Reasons Details */}
            {showBlockedDetails && progress.blocked > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-yellow-900 mb-2">Blocked Reasons:</h4>
                    <div className="space-y-1">
                        {Object.entries(progress.blocked_reasons).map(([reason, count]) => (
                            <div key={reason} className="flex justify-between text-sm">
                                <span className="text-yellow-800 capitalize">
                                    {reason.replace(/_/g, ' ')}
                                </span>
                                <span className="font-medium text-yellow-900">{count}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 text-xs text-yellow-700">
                        💡 Tip: Upload more documentation or link Jira/Confluence to unblock entities
                    </div>
                </div>
            )}

            {/* Summary Text */}
            <div className="mt-4 text-sm text-gray-600">
                {progress.queued > 0 && (
                    <div className="flex items-center space-x-2 text-blue-600">
                        <Loader className="w-4 h-4 animate-spin" />
                        <span>Analyzing {progress.queued} entities...</span>
                    </div>
                )}
                {progress.queued === 0 && progress.ready > 0 && (
                    <div className="text-green-600">
                        ✅ All entities analyzed! {progress.ready} specs ready for review.
                    </div>
                )}
            </div>
        </div>
    );
};

export default SpecReviewProgress;
